import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { firebaseApp } from '../../firebase.js';

const db = getFirestore(firebaseApp);

// ========== HELPER FUNCTIONS ==========

/**
 * Calculate trend based on recent scores
 */
const calculateTrend = (scores) => {
  if (scores.length < 2) return 'stable';

  const recentHalf = scores.slice(-Math.ceil(scores.length / 2));
  const olderHalf = scores.slice(0, Math.floor(scores.length / 2));

  const recentAvg = recentHalf.reduce((sum, s) => sum + s, 0) / recentHalf.length;
  const olderAvg = olderHalf.reduce((sum, s) => sum + s, 0) / olderHalf.length;

  const difference = recentAvg - olderAvg;

  if (difference > 5) return 'up';
  if (difference < -5) return 'down';
  return 'stable';
};

/**
 * Group scores by week for trend analysis
 */
const groupByWeek = (tests) => {
  const weekMap = new Map();

  tests.forEach(test => {
    const weekStart = new Date(test.completedAt);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay()); // Start of week
    weekStart.setHours(0, 0, 0, 0);

    const weekKey = weekStart.toISOString();

    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, { scores: [], count: 0 });
    }

    const weekData = weekMap.get(weekKey);
    weekData.scores.push(test.totalScore || test.score);
    weekData.count++;
  });

  const trends = [];
  const sortedWeeks = Array.from(weekMap.entries()).sort((a, b) =>
    new Date(a[0]).getTime() - new Date(b[0]).getTime()
  );

  sortedWeeks.forEach(([weekKey, data], index) => {
    const averageScore = data.scores.reduce((sum, s) => sum + s, 0) / data.scores.length;

    let category = 'stable';
    if (index > 0) {
      const prevAverage = sortedWeeks[index - 1][1].scores.reduce((sum, s) => sum + s, 0) /
                          sortedWeeks[index - 1][1].scores.length;

      if (averageScore > prevAverage + 5) category = 'improving';
      else if (averageScore < prevAverage - 5) category = 'declining';
    }

    trends.push({
      date: new Date(weekKey),
      averageScore: Math.round(averageScore),
      testCount: data.count,
      category
    });
  });

  return trends;
};

// ========== MAIN FUNCTIONS ==========

/**
 * Fetch all test results for a user
 */
export const getUserTestResults = async (userId) => {
  try {
    const resultsQuery = query(
      collection(db, 'jobseekerResults'),
      where('jobSeekerId', '==', userId),
      orderBy('completedAt', 'desc')
    );

    const snapshot = await getDocs(resultsQuery);

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        testId: data.testId || doc.id,
        testTitle: data.jobTitle || data.testTitle || 'Assessment',
        score: data.score || 0,
        totalScore: data.totalScore,
        mcqScore: data.mcqScore,
        interviewScore: data.interviewScore,
        completedAt: data.completedAt?.toDate() || new Date(),
        timeTaken: data.timeTaken
      };
    });
  } catch (error) {
    console.error('Error fetching test results:', error);
    return [];
  }
};

/**
 * Calculate comprehensive analytics for a user
 */
export const calculateUserAnalytics = async (userId) => {
  try {
    const tests = await getUserTestResults(userId);

    if (tests.length === 0) {
      return null;
    }

    // Basic stats
    const scores = tests.map(t => t.totalScore || t.score);
    const totalTests = tests.length;
    const averageScore = Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length);
    const highestScore = Math.max(...scores);
    const lowestScore = Math.min(...scores);

    // Time-based stats
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const testsThisWeek = tests.filter(t => t.completedAt >= weekAgo).length;
    const testsThisMonth = tests.filter(t => t.completedAt >= monthAgo).length;

    // Average time taken
    const testsWithTime = tests.filter(t => t.timeTaken);
    const averageTimeTaken = testsWithTime.length > 0
      ? Math.round(testsWithTime.reduce((sum, t) => sum + (t.timeTaken || 0), 0) / testsWithTime.length)
      : 0;

    // Performance trend
    const recentTests = tests.slice(0, Math.min(10, tests.length));
    const recentScores = recentTests.map(t => t.totalScore || t.score);
    const trendDirection = calculateTrend(scores);
    const performanceTrend = trendDirection === 'up' ? 'improving' :
                            trendDirection === 'down' ? 'declining' : 'stable';

    // Performance trends by week
    const recentPerformance = groupByWeek(tests.slice(0, 20)); // Last 20 tests

    // Skill breakdown (from test titles)
    const skillMap = new Map();

    tests.forEach(test => {
      // Extract skill from test title (basic implementation)
      const skill = test.testTitle.split(' ')[0] || 'General';

      if (!skillMap.has(skill)) {
        skillMap.set(skill, { scores: [], dates: [] });
      }

      const skillData = skillMap.get(skill);
      skillData.scores.push(test.totalScore || test.score);
      skillData.dates.push(test.completedAt);
    });

    const skillBreakdown = Array.from(skillMap.entries()).map(([skill, data]) => ({
      skillName: skill,
      testCount: data.scores.length,
      averageScore: Math.round(data.scores.reduce((sum, s) => sum + s, 0) / data.scores.length),
      lastTested: new Date(Math.max(...data.dates.map(d => d.getTime()))),
      trend: calculateTrend(data.scores)
    }));

    // Strong and weak skills
    const sortedSkills = [...skillBreakdown].sort((a, b) => b.averageScore - a.averageScore);
    const strongSkills = sortedSkills.slice(0, 3).map(s => s.skillName);
    const weakSkills = sortedSkills.slice(-3).reverse().map(s => s.skillName);

    // Category performance (MCQ vs Interview)
    const categoryPerformance = [];

    const mcqTests = tests.filter(t => t.mcqScore !== undefined);
    if (mcqTests.length > 0) {
      const mcqScores = mcqTests.map(t => t.mcqScore || 0);
      categoryPerformance.push({
        category: 'MCQ Tests',
        testsCompleted: mcqTests.length,
        averageScore: Math.round(mcqScores.reduce((sum, s) => sum + s, 0) / mcqScores.length),
        bestScore: Math.max(...mcqScores),
        worstScore: Math.min(...mcqScores)
      });
    }

    const interviewTests = tests.filter(t => t.interviewScore !== undefined);
    if (interviewTests.length > 0) {
      const interviewScores = interviewTests.map(t => t.interviewScore || 0);
      categoryPerformance.push({
        category: 'AI Interviews',
        testsCompleted: interviewTests.length,
        averageScore: Math.round(interviewScores.reduce((sum, s) => sum + s, 0) / interviewScores.length),
        bestScore: Math.max(...interviewScores),
        worstScore: Math.min(...interviewScores)
      });
    }

    const analytics = {
      userId,
      totalTests,
      averageScore,
      highestScore,
      lowestScore,
      testsThisWeek,
      testsThisMonth,
      averageTimeTaken,
      performanceTrend,
      strongSkills,
      weakSkills,
      recentPerformance,
      skillBreakdown,
      categoryPerformance,
      lastUpdated: new Date()
    };

    // Cache analytics in Firestore
    await setDoc(doc(db, 'userAnalytics', userId), {
      ...analytics,
      lastUpdated: serverTimestamp()
    });

    return analytics;
  } catch (error) {
    console.error('Error calculating analytics:', error);
    return null;
  }
};

/**
 * Get cached analytics or calculate fresh
 */
export const getUserAnalytics = async (userId, forceRefresh = false) => {
  try {
    if (!forceRefresh) {
      // Try to get cached analytics first
      const cachedDoc = await getDoc(doc(db, 'userAnalytics', userId));

      if (cachedDoc.exists()) {
        const data = cachedDoc.data();
        const lastUpdated = data.lastUpdated?.toDate() || new Date(0);
        const hoursSinceUpdate = (Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60);

        // Use cache if less than 1 hour old
        if (hoursSinceUpdate < 1) {
          return {
            ...data,
            lastUpdated: lastUpdated
          };
        }
      }
    }

    // Calculate fresh analytics
    return await calculateUserAnalytics(userId);
  } catch (error) {
    console.error('Error getting user analytics:', error);
    return null;
  }
};

/**
 * Get performance comparison with platform average
 */
export const getPerformanceComparison = async (userId) => {
  try {
    const userAnalytics = await getUserAnalytics(userId);
    if (!userAnalytics) return null;

    // Get all users' average scores (simplified - in production, use aggregated data)
    const allResultsSnapshot = await getDocs(collection(db, 'jobseekerResults'));
    const allScores = allResultsSnapshot.docs.map(doc => doc.data().totalScore || doc.data().score || 0);

    const platformAverage = Math.round(
      allScores.reduce((sum, s) => sum + s, 0) / allScores.length
    );

    // Calculate percentile
    const scoresBelowUser = allScores.filter(s => s < userAnalytics.averageScore).length;
    const percentile = Math.round((scoresBelowUser / allScores.length) * 100);
    const betterThanPercent = percentile;

    return {
      userAverage: userAnalytics.averageScore,
      platformAverage,
      percentile,
      betterThanPercent
    };
  } catch (error) {
    console.error('Error getting performance comparison:', error);
    return null;
  }
};

